/*
 * Copyright (C) Ivo Ladage-van Doorn - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Ivo Ladage-van Doorn <ivol37@gmail.com>, 2024
 */
import React from "react";
import { Person, Event, Mariage, BackgroundInfo, EventType, Parent } from "./types";

/**
 * Renders a parent, including mariages and children.
 *
 * @param {Parent} parent The parent to render
 * @param {number} index The key
 */
export class ParentComponent extends React.Component<{ parent: Parent; index: number }, {}> {
  render(): React.ReactNode {
    const parent = this.props.parent;
    const index = this.props.index;
    return (
      <li key={index}>
        <PersonComponent person={parent} showAsMain={true} />
        <br />
        {parent.mariages?.map((mariage: Mariage, index: number) => (
          <div key={index}>
            <MariageComponent mariage={mariage} />
            {mariage.partner && mariage.children ? "Uit dit huwelijk:" : mariage.children ? "Kinderen:" : ""}
            <ul>
              {mariage.children?.map((child: Person, index2: number) => (
                <li key={index2}>
                  <PersonComponent person={child} showAsMain={child.mainAncestor} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </li>
    );
  }
}

/**
 * Renders a person and basic information about this person.
 *
 * @param {Person} person The person to render
 */
export class PersonComponent extends React.Component<{ person: Person; showAsMain?: boolean }, {}> {
  render(): React.ReactNode {
    const person = this.props.person;

    return (
      <span>
        <span className={this.props.showAsMain ? "name" : ""}>{person.name}</span>
        <span className="events">
          <EventComponent event={person.birth} type="birth" />
          <EventComponent event={person.baptism} type="baptism" />
          <EventComponent event={person.death} type="death" />
          <EventComponent event={person.burrial} type="burrial" />
        </span>
        <BackgroundInfoComponent info={person.backgroundInfo} />
      </span>
    );
  }
}

/**
 * Renders background information with optional sources.
 *
 * @param {BackgroundInfo} info The background information to render
 */
export class BackgroundInfoComponent extends React.Component<{ info?: BackgroundInfo }, {}> {
  render(): React.ReactNode {
    const info = this.props.info;
    if (!info) return;
    return (
      <span className="backgroundInfo">
        <br />
        <span dangerouslySetInnerHTML={{ __html: info.text }} />
        {info.sources?.map((source, index) => (
          <sup key={index}>
            <a onClick={() => scrollToFootnote(source.index)}>{source.index}</a>
          </sup>
        ))}
      </span>
    );
  }
}

/**
 * Renders basic information about an event, like birth, baptism, death, etc.
 *
 * @param {Event} event The event
 * @param {EventType} The type of the event
 */
export class EventComponent extends React.Component<{ event?: Event; type: EventType }, {}> {
  render(): React.ReactNode {
    const event = this.props.event;
    if (!event) return "";
    let icon = "";
    if (this.props.type === "birth") {
      icon = "*";
    } else if (this.props.type === "baptism") {
      icon = "〰";
    } else if (this.props.type === "death") {
      icon = "✝︎";
    } else if (this.props.type === "burrial") {
      icon = "[]";
    }
    return (
      <span className="event">
        {icon} {toDateAndCity(event as Event)}
        {event.sources?.map((source, index) => (
          <sup key={index}>
            <a onClick={() => scrollToFootnote(source.index)}>{source.index}</a>
          </sup>
        ))}
      </span>
    );
  }
}

/**
 * Renders basic information about a mariage
 *
 * @param {Mariage} mariage The mariage
 */
export class MariageComponent extends React.Component<{ mariage?: Mariage }, {}> {
  render(): React.ReactNode {
    const mariage = this.props.mariage;
    if (!mariage || !mariage.partner) return "";
    return (
      <div>
        ⚭ {mariage.date && mariage.date} {mariage.city && " te " + mariage.city}
        {mariage.sources?.map((source, index) => (
          <sup key={index}>
            <a onClick={() => scrollToFootnote(source?.index)}>{source.index}</a>
          </sup>
        ))}
        {mariage.partner && " met "}
        <PersonComponent person={mariage?.partner} />
      </div>
    );
  }
}

const toDateAndCity = (event?: Event) => {
  let result = "";
  if (!event || (!event.date && !event.city)) {
    return "?";
  }
  if (event.date) {
    result += event.date;
  }
  if (event.city) {
    result += " te " + event.city;
  }
  return result;
};

const scrollToFootnote = (index?: number) => {
  const element = document.getElementById("footnote-" + index);
  const yOffset = -130;
  if (element) {
    const pageContainer = document.querySelector(".page-container") as HTMLDivElement;
    const y = element.getBoundingClientRect().top + pageContainer.scrollTop + yOffset;
    pageContainer.scrollTo({ top: y, behavior: "smooth" });
  }
};
